<template>
    <div v-if="show && loaded" class="nevs-popup-container">
        <div class="nevs-popup">
            <div class="popup-form">
                <NevsSelect v-model="membershipId" :ajax="'select/memberships'" :error="validation.membership_id"
                            :label="'*' + $LANG.Get('fields.membership') + ':'"></NevsSelect>
                <NevsDateField v-model="startDate" :error="validation.start_date"
                               :label="'*' + $LANG.Get('fields.startDate') + ':'"></NevsDateField>
                <!-- Only when editing: a new membership gets its end date and entry limit from the membership. -->
                <template v-if="personMembership !== null">
                    <NevsDateField v-model="endDate" :error="validation.end_date"
                                   :label="'*' + $LANG.Get('fields.endDate') + ':'"></NevsDateField>
                    <NevsNumberField :decimal-places="0" v-model="maxEntries" :error="validation.max_entries"
                                     :hint="$LANG.Get('labels.maxEntriesHint')"
                                     :label="$LANG.Get('fields.maxEntries') + ':'"></NevsNumberField>
                    <div class="membership-disclaimer">
                        <i class="fa-solid fa-circle-info"></i>
                        <span>{{ $LANG.Get('texts.personMembershipEndDateDisclaimer') }}</span>
                    </div>
                    <div v-if="personMembership.depleted_date_display" class="membership-disclaimer">
                        <i class="fa-solid fa-circle-info"></i>
                        <span>{{ depletedText }}</span>
                    </div>
                </template>
            </div>
            <div class="nevs-popup-buttons">
                <NevsButton class="error" @click="cancelClick()">
                    <i class="fa-solid fa-xmark"></i>
                    {{ $LANG.Get('buttons.cancel') }}
                </NevsButton>
                <NevsButton class="success" @click="okClick()">
                    <i class="fa-solid fa-check"></i>
                    {{ $LANG.Get('buttons.ok') }}
                </NevsButton>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";

export default {
    name: "ModalEditPersonMembership",
    components: {NevsSelect, NevsDateField, NevsNumberField, NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        },
        personId: {
            type: [Number, String],
            default: null
        },
        personMembership: {
            type: Object,
            default: null
        }
    },
    emits: ['done'],
    data() {
        return {
            loaded: false,
            membershipId: null,
            startDate: '',
            endDate: '',
            // The end date as loaded: it is sent only when staff changed it, so changing just the start date or
            // the membership still recalculates the end date on the server.
            originalEndDate: '',
            maxEntries: 0,
            validation: {
                membership_id: '',
                start_date: '',
                end_date: '',
                max_entries: ''
            }
        }
    },
    computed: {
        depletedText() {
            return this.$LANG.Get('texts.personMembershipDepleted')
                .replace('%date%', this.personMembership.depleted_date_display);
        }
    },
    watch: {
        show() {
            this.loaded = false;
            this.validation.membership_id = '';
            this.validation.start_date = '';
            this.validation.end_date = '';
            this.validation.max_entries = '';
            if (this.show) {
                if (this.personMembership !== null) {
                    this.membershipId = this.personMembership.membership_id;
                    this.startDate = this.personMembership.start_date;
                    this.endDate = this.personMembership.end_date;
                    this.originalEndDate = this.personMembership.end_date;
                    this.maxEntries = this.personMembership.max_entries || 0;
                } else {
                    this.membershipId = null;
                    this.startDate = moment().format('YYYY-MM-DD');
                    this.endDate = '';
                    this.originalEndDate = '';
                    this.maxEntries = 0;
                }
                this.$nextTick(() => {
                    this.loaded = true;
                });
            }
        }
    },
    methods: {
        cancelClick() {
            this.$emit('done', false);
        },
        okClick() {
            let valid = true;
            if (this.membershipId === null) {
                this.validation.membership_id = this.$LANG.Get('labels.mandatoryField');
                valid = false;
            } else {
                this.validation.membership_id = '';
            }
            if (this.startDate === '' || this.startDate === null) {
                this.validation.start_date = this.$LANG.Get('labels.mandatoryField');
                valid = false;
            } else {
                this.validation.start_date = '';
            }
            let endDateChanged = this.personMembership !== null && this.endDate !== this.originalEndDate;
            if (this.personMembership !== null && (this.endDate === '' || this.endDate === null)) {
                this.validation.end_date = this.$LANG.Get('labels.mandatoryField');
                valid = false;
            } else if (endDateChanged && this.startDate && this.endDate < this.startDate) {
                this.validation.end_date = this.$LANG.Get('labels.endDateBeforeStartDate');
                valid = false;
            } else {
                this.validation.end_date = '';
            }
            if (this.personMembership !== null && this.maxEntries < 0) {
                this.validation.max_entries = this.$LANG.Get('labels.errorNegativeValue');
                valid = false;
            } else {
                this.validation.max_entries = '';
            }
            if (!valid) {
                return;
            }

            let vm = this;
            let method = this.personMembership === null ? 'post' : 'put';
            let action = this.personMembership === null
                ? 'person-memberships'
                : 'person-memberships/' + this.personMembership.id;
            let payload = {
                person_id: this.personId,
                membership_id: this.membershipId,
                start_date: this.startDate
            };
            if (this.personMembership !== null) {
                payload.max_entries = this.maxEntries;
                if (endDateChanged) payload.end_date = this.endDate;
            }
            this.$API.APICall(method, action, payload, (data, success) => {
                if (success) {
                    vm.$emit('done', true);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.nevs-popup {
    max-width: 500px;
}

.popup-form {
    padding: 10px;
}

.membership-disclaimer {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background-color: $warning-surface;
    border: 1px solid $warning-border;
    color: $warning-ink;
    border-radius: 5px;
    padding: 12px 14px;
    margin-top: 15px;
    font-size: 0.9em;
    line-height: 1.45;
}

.membership-disclaimer i {
    margin-top: 2px;
    flex-shrink: 0;
}
</style>
